import React, { useState } from 'react';
import { createDonationCheckoutSession } from '../../services/stripeService';

const DonationBubbleTab = ({ profileId, profileName, donationMessage, onClose, accentColor = '#749555', isOwner = false, profileUsername = null }) => {
  const [selectedAmount, setSelectedAmount] = useState(2000); // Default to $20
  const [customAmount, setCustomAmount] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');

  const presetAmounts = [200, 500, 2000, 10000]; // $2, $5, $20, $100 in cents

  const handleCopyDonateLink = async () => {
    const currentOrigin = window.location.origin;
    const currentHostname = window.location.hostname;

    // Check if on subdomain (e.g., username.chessweb.site)
    const isSubdomain = currentHostname.includes('.chessweb.site') &&
                       currentHostname !== 'chessweb.site' &&
                       currentHostname !== 'www.chessweb.site';

    let donateUrl;
    if (isSubdomain) {
      // Subdomain format: username.chessweb.site/donate
      donateUrl = `${currentOrigin}/donate`;
    } else {
      // Regular format: chessweb.site/me/username/donate
      donateUrl = `${currentOrigin}/me/${profileUsername}/donate`;
    }

    try {
      await navigator.clipboard.writeText(donateUrl);
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    setSelectedAmount('custom');
    setError('');
  };

  const getAmountInCents = () => {
    if (selectedAmount === 'custom') {
      const amount = parseFloat(customAmount);
      if (isNaN(amount) || amount < 2) {
        return null;
      }
      return Math.round(amount * 100);
    }
    return selectedAmount;
  };

  const handleDonate = async () => {
    const amountCents = getAmountInCents();

    if (!amountCents) {
      setError('Please enter a valid amount (minimum $2.00)');
      return;
    }

    if (amountCents < 200) {
      setError('Minimum donation is $2.00');
      return;
    }

    if (amountCents > 100000) {
      setError('Maximum donation is $1000.00');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { sessionUrl } = await createDonationCheckoutSession(
        profileId,
        amountCents,
        donorEmail,
        donorMessage
      );

      // Redirect to Stripe Checkout
      window.location.href = sessionUrl;
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.message || 'Failed to process donation. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto mb-8">
      <div className="bg-gray-900 border-2 rounded-2xl shadow-lg transition-all duration-200" style={{ borderColor: accentColor }}>
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div
              className="w-10 h-10 sm:w-8 sm:h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${accentColor}30` }}
            >
              <i className="fas fa-sack-dollar text-xl" style={{ color: accentColor }}></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Support {profileName}</h3>
              <p className="text-sm text-gray-300">Make a one-time donation (USD)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-red-900/30 hover:bg-red-900/50 flex items-center justify-center transition-colors border border-red-500"
            title="Close"
          >
            <i className="fas fa-times text-red-400"></i>
          </button>
        </div>

        {/* Content Section */}
        <div className="p-6">
          {/* Custom Message */}
          {donationMessage && (
            <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <p className="text-gray-300 text-sm text-center">{donationMessage}</p>
            </div>
          )}

          {/* All Inputs in Single Grid */}
          <div className="mb-6">
            
            <div className="grid grid-cols-2 gap-3">
              {/* Preset Amount Buttons */}
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleAmountSelect(amount)}
                  className={`px-4 py-3 rounded-lg font-semibold transition-all border-2 ${
                    selectedAmount === amount
                      ? 'text-white'
                      : 'bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-600'
                  }`}
                  style={
                    selectedAmount === amount
                      ? { backgroundColor: accentColor, borderColor: accentColor }
                      : {}
                  }
                >
                  ${(amount / 100).toFixed(2)}
                </button>
              ))}

              {/* Custom Amount Input - spans 2 columns */}
              <div className="col-span-2">
                <div className="relative">
                  <span className="absolute left-4 top-3 text-gray-400 text-lg">$</span>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    min="2"
                    max="500"
                    step="0.01"
                    placeholder="Custom Amount"
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border-2 border-gray-700 focus:border-chess-green focus:outline-none"
                  />
                </div>
               
              </div>

              
            </div>
          </div>

          {/* Donor Message (Optional) */}
          <div className="">
         
            <textarea
              value={donorMessage}
              onChange={(e) => setDonorMessage(e.target.value)}
              maxLength={500}
              rows={1}
              placeholder="Optional: Send a message."
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border-2 border-gray-700 focus:border-chess-green focus:outline-none resize-none"
            />
          
          </div>
          {/* Email Input - spans 2 columns */}
          <div className="col-span-2">
                <input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="Optional: Email for receipt."
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border-2 border-gray-700 focus:border-chess-green focus:outline-none"
                />
              
              </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-600/50 rounded-lg">
              <p className="text-red-200 text-sm text-center">
                <i className="fas fa-exclamation-circle mr-2"></i>
                {error}
              </p>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex space-x-3">

            <button
              onClick={handleDonate}
              disabled={loading || !getAmountInCents()}
              className="flex-1 px-6 py-3 text-white rounded-lg hover:opacity-90 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: accentColor }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-heart mr-2"></i>
                  Support with {getAmountInCents() ? `$${(getAmountInCents() / 100).toFixed(2)}` : '$0.00'}
                </>
              )}
            </button>
          </div>

          {/* Fee Disclosure */}
          <div className="mt-3">
            <p className="text-gray-500 text-xs text-center">
              No platform fees — only standard payment processing applies.
            </p>
          </div>

          {/* Share Link - Only show for owner */}
          {isOwner && profileUsername && (
            <div className="mt-3 text-center">
              <button
                onClick={handleCopyDonateLink}
                className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                {copyFeedback || 'share link'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationBubbleTab;
